import { required, resolveNopeRefs } from './shared';
import type { FieldError, NopeContext, ObjectShape, ValidationRule } from './shared';
import { isDomainValid } from '../validation/domain';

const min =
  (length: number, message: string): ValidationRule =>
  (value, context) => {
    const resolvedValue = resolveNopeRefs(value, context) as string;

    return resolvedValue && resolvedValue.length <= length && message;
  };

const max =
  (length: number, message: string): ValidationRule =>
  (value, context) => {
    const resolvedValue = resolveNopeRefs(value, context) as string;

    return resolvedValue && resolvedValue.length >= length && message;
  };

// Linear-time, ReDoS-safe email validation (CWE-185 / CP-17011).
// The previous implementation used a single regex whose local-part construct
// `([^...]+(\.[^...]+)*)|(".+")` contains nested/adjacent quantifiers that can
// trigger catastrophic backtracking on crafted malformed input. We replace it
// with a structural check: split on the last `@`, then validate the local and
// domain parts with bounded, non-backtracking patterns. Every check below runs
// in time linear in the input length.
const LOCAL_ATOM_CHAR = /^[^<>()[\]\\.,;:\s@"]+$/; // no nested quantifiers
const DOMAIN_LABEL = /^[a-zA-Z0-9-]+$/;
const DOMAIN_TLD = /^[a-zA-Z]{2,}$/;
const IPV4_LITERAL = /^\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\]$/;

const isEmailLocalPartValid = (local: string): boolean => {
  if (local.length === 0) return false;

  // Quoted local-part: `"..."`. Preserve the previous validator's behavior
  // (old regex branch `".+"`): require non-empty quoted content and reject
  // CR/LF, which `.` did not match without the `s` flag. The scan below is a
  // single linear pass with no nested quantifiers, so it stays ReDoS-safe.
  if (local.length >= 2 && local.startsWith('"') && local.endsWith('"')) {
    const inner = local.slice(1, -1);
    if (inner.length === 0) return false; // reject empty quoted local-part (`""`)
    if (inner.includes('\n') || inner.includes('\r')) return false; // reject CR/LF
    return true;
  }

  // Unquoted: dot-separated atoms; no empty atoms (no leading/trailing/double dots).
  const atoms = local.split('.');
  return atoms.every((atom) => atom.length > 0 && LOCAL_ATOM_CHAR.test(atom));
};

const isEmailDomainValid = (domainPart: string): boolean => {
  if (domainPart.length === 0) return false;

  // IPv4 literal domain, e.g. `[127.0.0.1]`.
  if (IPV4_LITERAL.test(domainPart)) {
    return true;
  }

  // Dotted hostname with a 2+ alpha TLD, e.g. `example.com`.
  const labels = domainPart.split('.');
  if (labels.length < 2) return false;

  const tld = labels[labels.length - 1];
  if (!DOMAIN_TLD.test(tld)) return false;

  return labels.every((label) => label.length > 0 && DOMAIN_LABEL.test(label));
};

const isEmailValid = (input: string): boolean => {
  if (typeof input !== 'string' || input.length === 0) return false;

  const atIndex = input.lastIndexOf('@');
  if (atIndex <= 0 || atIndex === input.length - 1) return false;

  const local = input.slice(0, atIndex);
  const domainPart = input.slice(atIndex + 1);

  return isEmailLocalPartValid(local) && isEmailDomainValid(domainPart);
};

const email =
  (message: string): ValidationRule =>
  (value, context) => {
    const resolvedValue = resolveNopeRefs(value, context) as string;

    return !isEmailValid(resolvedValue) && message;
  };

const url =
  (message: string): ValidationRule =>
  (value, context) => {
    const resolvedValue = resolveNopeRefs(value, context) as string;

    if (!resolvedValue) {
      return undefined;
    }

    try {
      new URL(resolvedValue);
      return undefined;
    } catch {
      return message;
    }
  };

const includes =
  (options: unknown[], message: string): ValidationRule =>
  (value, context) => {
    const resolvedOptions = resolveNopeRefs(options, context) as unknown[];
    const resolvedValue = resolveNopeRefs(value, context);

    return !resolvedOptions.includes(resolvedValue) && message;
  };

const domain =
  (message: string): ValidationRule =>
  (value, context) => {
    const resolvedValue = resolveNopeRefs(value, context) as string;

    return !isDomainValid(resolvedValue) && message;
  };

class NopeString {
  validationRules: ValidationRule[] = [];

  required(message = 'This field is required'): this {
    this.validationRules.push(required(message));

    return this;
  }

  max(length: number, message = `Input can't be longer than ${length} characters`): this {
    this.validationRules.push(max(length, message));

    return this;
  }

  min(length: number, message = `Input can't be shorter than ${length} characters`): this {
    this.validationRules.push(min(length, message));

    return this;
  }

  email(message = 'Input must be a valid email'): this {
    this.validationRules.push(email(message));

    return this;
  }

  url(message = 'Input must be a valid URL'): this {
    this.validationRules.push(url(message));

    return this;
  }

  domain(message = 'Please enter a valid domain (e.g., example.com, *.example.com)'): this {
    this.validationRules.push(domain(message));

    return this;
  }

  oneOf(options: unknown[], message = `Input must be one of ${options.join(', ')}`): this {
    this.validationRules.push(includes(options, message));

    return this;
  }

  test(fn: ValidationRule): this {
    this.validationRules.push(fn);

    return this;
  }

  validate(entry: unknown, context?: NopeContext, objectShape?: ObjectShape): FieldError {
    for (const fn of this.validationRules) {
      const error = fn(entry, context, objectShape);

      if (error) {
        return error;
      }
    }
  }
}

export default NopeString;
